export const INCOME_TYPE_VALUES = [
  "wages",
  "dividend",
  "rental",
  "pension",
  "social_security",
  "gig",
  "interest",
  "child_support",
  "disability",
  "military",
  "unemployment",
  "other",
] as const;

export type IncomeType = (typeof INCOME_TYPE_VALUES)[number];
export type EarnedIncomeType = "wages" | "gig" | "military";

const EARNED_INCOME_TYPES: ReadonlySet<IncomeType> = new Set([
  "wages",
  "gig",
  "military",
]);

export const INCOME_TYPE_LABELS: Record<IncomeType, string> = {
  wages: "Wages & Salary",
  dividend: "Dividends",
  rental: "Rental Income",
  pension: "Pension",
  social_security: "Social Security",
  gig: "Gig Economy",
  interest: "Interest Earned",
  child_support: "Child Support",
  disability: "Disability Income",
  military: "Military Pay",
  unemployment: "Unemployment",
  other: "Other Income",
};

export const INCOME_TYPE_OPTIONS = INCOME_TYPE_VALUES.map((value) => ({
  value,
  label: INCOME_TYPE_LABELS[value],
}));

export function isEarnedIncomeType(
  type: string | null | undefined,
): type is EarnedIncomeType {
  return EARNED_INCOME_TYPES.has(type as IncomeType);
}

/**
 * Backward-compatible name for callers that treat employment income as wages.
 * Prefer isEarnedIncomeType in new code.
 */
export function isWageIncomeType(
  type: string | null | undefined,
): type is EarnedIncomeType {
  return isEarnedIncomeType(type);
}

export function isRetirementGatedIncomeType(
  type: string | null | undefined,
): type is EarnedIncomeType {
  return isEarnedIncomeType(type);
}

export function isSocialSecurityIncomeType(
  type: string | null | undefined,
): type is "social_security" {
  return type === "social_security";
}

export function incomeTypeRequiresPerson(
  type: string | null | undefined,
): boolean {
  return isEarnedIncomeType(type);
}

export function getIncomeTypeLabel(type: string): string {
  return (INCOME_TYPE_LABELS as Record<string, string>)[type] ?? type;
}
